import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobPath } from '../components/driver/useDriverJob';
import { formatGBP } from '../lib/currency';
import { pastOrdersApi } from '../api/pastOrders';
import { riderWork } from '../api/riderWork';
import { formatOfferTime } from '../lib/driverIncomingBookings';
import { getDriverSession } from '../lib/driverSession';
import { isCarried, orderRowFromApi, recentFromApi } from '../lib/riderWork';
import { pastDay, pastFare, usePastOrders } from '../lib/usePastOrders';
import './driverPortal.css';
import './driverOrdersPremium.css';

const TABS = [
  { id: 'active', label: 'Active' },
  { id: 'history', label: 'History' },
];

function lineStatusClass(label) {
  const s = String(label || '').toLowerCase();
  if (s.includes('drop') || s.includes('delivering') || s.includes('transit')) {
    return 'dplOrdSt dplOrdSt--enroute';
  }
  if (s.includes('pickup') || s.includes('heading')) return 'dplOrdSt dplOrdSt--pickup';
  return 'dplOrdSt dplOrdSt--pickup';
}

/**
 * The rider's jobs: the one carried now, the finished ones, and under them
 * the orders from the app before InGo, kept as history (InGo ADR 0034):
 * never in the earnings or what the rider owes.
 */
export default function DriverOrdersPage({ pastOrders = pastOrdersApi }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('active');
  const old = usePastOrders(pastOrders.rider);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const driverId = getDriverSession()?.id || null;

  const load = useCallback(async () => {
    setLoadErr('');
    if (!driverId) {
      setActive([]);
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // The rider's jobs on InGo's API: the one carried now, and the finished ones.
      const jobs = await riderWork.deliveries();
      setActive(jobs.filter(isCarried).map((d) => orderRowFromApi(d, formatOfferTime)));
      setHistory(
        jobs
          .filter((d) => d.status === 'delivered' || d.status === 'completed')
          .map(recentFromApi)
          .map((r) => ({ id: r.id, ref: r.ref, to: r.to, at: r.t, amount: r.amt })),
      );
    } catch (e) {
      setActive([]);
      setHistory([]);
      setLoadErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  const openActive = (row) => {
    navigate(jobPath('active-delivery', row.order), { state: { order: row.order } });
  };

  return (
    <div className="dpl dpl--premium" role="main" aria-label="Orders">
      <header className="dpl__hero">
        <h1 className="dplH dpl__title">Orders</h1>
        <p className="dplIntro dpl__subtitle">Active jobs and your recent delivery history.</p>
      </header>

      <div className="dpl__tabsWrap">
        <div className="dplTabs" role="tablist" aria-label="Order lists">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={tab === id ? 'dplTab dplTab--on' : 'dplTab'}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="dplBody">
        {loadErr ? (
          <p className="dpl__empty dpl__empty--err" role="alert">
            {loadErr}
          </p>
        ) : null}

        {tab === 'active' && (
          <>
            {loading ? (
              <p className="dpl__empty">Loading active deliveries…</p>
            ) : active.length === 0 ? (
              <p className="dpl__empty">No active deliveries. Go online on Home to receive offers.</p>
            ) : (
              active.map((row) => (
                <div key={row.order?.deliveryId || row.id} className="dplOrdCard dplOrdCard--act">
                  <div className="dplOrdTop">
                    <p className="dplOrdId">{row.id}</p>
                    <span className={lineStatusClass(row.lineStatus)}>{row.lineStatus}</span>
                  </div>

                  <div className="dplRoute">
                    <div className="dplRoute__row dplRoute__row--pick">
                      <span className="dplRoute__dot" aria-hidden />
                      <p className="dplRoute__addr">{row.from}</p>
                    </div>
                    <div className="dplRoute__row dplRoute__row--drop">
                      <span className="dplRoute__dot" aria-hidden />
                      <p className="dplRoute__addr">{row.to}</p>
                    </div>
                  </div>

                  <p className="dplOrdMeta">
                    {row.pkg}
                    {' · '}
                    {row.lineTime}
                  </p>

                  <div className="dplOrdPayout">
                    <span className="dplOrdPayoutLbl">Payout</span>
                    <span className="dplOrdPayoutAmt">{formatGBP(row.amount)}</span>
                  </div>

                  <button type="button" className="dplOrdGo" onClick={() => openActive(row)}>
                    Continue Delivery
                  </button>
                </div>
              ))
            )}
          </>
        )}

        {tab === 'history' && (
          <>
            {loading ? (
              <p className="dpl__empty">Loading history…</p>
            ) : history.length === 0 ? (
              old.orders.length > 0 ? null : <p className="dpl__empty">No completed deliveries yet.</p>
            ) : (
              history.map((r) => (
                <div key={r.id} className="dplOrdCard dplOrdCard--hist">
                  <div className="dplOrdTop">
                    <p className="dplOrdId">{r.ref}</p>
                    <span className="dplOrdSt dplOrdSt--dn">Delivered</span>
                  </div>
                  <p className="dplOrdHistTo">{r.to}</p>
                  <div className="dplOrdPayout">
                    <span className="dplOrdPayoutLbl">{formatOfferTime(r.at)}</span>
                    <span className="dplOrdPayoutAmt">{formatGBP(r.amount)}</span>
                  </div>
                </div>
              ))
            )}

            {old.orders.length > 0 || old.error ? (
              <section aria-label="From the app before InGo">
                <h2 className="dplPastTitle">From the app before InGo</h2>
                <p className="dplPastNote">Not in your earnings or wallet.</p>
                {old.error ? (
                  <p className="dpl__empty dpl__empty--err" role="alert">
                    {old.error}
                  </p>
                ) : null}
                {old.orders.map((o) => (
                  <div key={`past-${o.id}`} className="dplOrdCard dplOrdCard--hist">
                    <div className="dplOrdTop">
                      <p className="dplOrdId">{o.reference}</p>
                      <span className={o.status === 'cancelled' ? 'dplOrdSt dplOrdSt--cx' : 'dplOrdSt dplOrdSt--dn'}>{o.status_label}</span>
                    </div>
                    <p className="dplOrdHistTo">{o.dropoff}</p>
                    <div className="dplOrdPayout">
                      <span className="dplOrdPayoutLbl">{pastDay(o)}</span>
                      <span className="dplOrdPayoutAmt">{pastFare(o)}</span>
                    </div>
                  </div>
                ))}
                {old.next ? (
                  <button type="button" className="dplTab" disabled={old.more} onClick={old.showOlder}>
                    {old.more ? 'Loading…' : 'Show older'}
                  </button>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
